import { useMemo } from "react";
import { isAfter } from "date-fns";
import { useTranslation } from "react-i18next";
import { CalendarIcon } from "@components/icons";
import { Tooltip } from "@components/Tooltip/Tooltip";
import { BillState, type IBillData } from "@services/overview/types";
import { capitalizeFirstLetter, isNullOrUndefined } from "@utils/common";
import { formatDate } from "@utils/units";
import { COLORS } from "@/tailwindColors";

interface IDueDateBadgeProps {
  billData: IBillData;
}

export const DueDateBadge: React.FC<IDueDateBadgeProps> = (props) => {
  const { billData } = props;
  const { t } = useTranslation();

  const colors = useMemo(() => {
    if (billData.billStateId === BillState.Paid) {
      return { background: COLORS.green.light, text: COLORS.green.dark };
    }

    // if due date is still in the future
    if (isAfter(new Date(billData.dueDate), new Date())) {
      return { background: COLORS.orange.light, text: COLORS.orange.dark };
    }

    return { background: COLORS.red.pastel, text: COLORS.red.dark };
  }, [billData]);

  if (!billData.canHaveDueDate) {
    return null;
  }

  if (isNullOrUndefined(billData.dueDate)) {
    return (
      <Tooltip content={t("bill.dueDate.enterDueDate")}>
        <div className="flex size-9 items-center justify-center rounded-lg bg-blue-hover text-blue-dark">
          <CalendarIcon className="size-4" />
        </div>
      </Tooltip>
    );
  }

  return (
    <Tooltip
      content={t("bill.dueDate.payUntil", {
        date: formatDate(billData.dueDate, t("bill.dueDate.payUntilDate")),
      })}
    >
      <div
        className="flex size-9 select-none flex-col overflow-hidden rounded-lg border"
        style={{ borderColor: colors.background }}
      >
        <div className="flex min-h-[1.125rem] items-center justify-center">
          <span
            className="text-xs font-semibold leading-3"
            style={{ color: colors.text }}
          >
            {formatDate(billData.dueDate, "d")}
          </span>
        </div>
        <div
          className="flex flex-grow items-center justify-center"
          style={{ background: colors.background }}
        >
          <span
            className="text-[0.5625rem] font-semibold leading-[0.5625rem]"
            style={{ color: colors.text }}
          >
            {capitalizeFirstLetter(
              formatDate(billData.dueDate, t("bill.dueDate.weekDate")),
            )}
            .
          </span>
        </div>
      </div>
    </Tooltip>
  );
};
